import Component from '@glimmer/component';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { trackedFunction } from 'reactiveweb/function';
import { t } from 'ember-intl';
import pageTitle from 'ember-page-title/helpers/page-title';
import { SkipCache } from '@warp-drive/core-types/request';
import DashboardViewPage from 'tio-reporting/components/dashboard-view/page';
import { getReportRequest, reportFromResource } from 'tio-reporting/requests/report';
import PageHeader from 'tio-ui/components/page-header';
import { VStack } from 'tio-ui/components/layout';
import type { Store as StoreService } from '@warp-drive/core';
import type RouterService from '@ember/routing/router-service';
import type { ReportsExtension as Report } from 'tio-data/data/resources/reports.ext';
import type SessionContextService from 'tio-employee/services/session-context';
import { reportingCompanyId } from 'tio-employee/utils/reporting-company';

interface DashboardViewerRouteSignature {
  Args: {
    model: { dashboardId: string };
  };
}

class DashboardViewerRouteComponent extends Component<DashboardViewerRouteSignature> {
  @service declare store: StoreService;
  @service declare router: RouterService;
  @service declare sessionContext: SessionContextService;

  dashboardData = trackedFunction(this, async () => {
    const request = getReportRequest(this.args.model.dashboardId);
    request.cacheOptions = { [SkipCache]: true };
    const { content } = await this.store.request(request);
    // Plain, camel-keyed object (searchConfig parsed) — the shape DashboardViewPage reads.
    const data = (content as { data: Parameters<typeof reportFromResource>[0] }).data;
    return reportFromResource(data) as unknown as Report;
  });

  /** Anything but a dashboard has no widget grid to render, so it reads as not found here. */
  get dashboard() {
    // trackedFunction keeps its last resolved value after a rejection, so a failed load must not fall back to it.
    if (this.dashboardData.isError) return null;
    const report = this.dashboardData.value;
    return (report?.reportType || '').toLowerCase() === 'dashboard' ? report : null;
  }

  /** Every widget query runs against this; without it a Global dashboard runs unscoped and draws empty. */
  get companyId() {
    const { rolesForCurrentCompany, currentCompany } = this.sessionContext;
    return reportingCompanyId(rolesForCurrentCompany, currentCompany?.id ?? undefined);
  }

  get companyName() {
    return this.sessionContext.currentCompany?.name;
  }

  /** A session with no reporting role here gets nothing, as the Reports page itself does. */
  get viewableDashboard() {
    return this.companyId ? this.dashboard : null;
  }

  handleBack = () => {
    this.router.transitionTo('authenticated.admin.reporting.index');
  };

  <template>
    {{pageTitle (t "reporting.dashboards")}}
    <VStack>
      {{#if this.dashboardData.isLoading}}
        <PageHeader @a11yTitle={{t "reporting.dashboards"}} @sticky={{false}} />
        <div class="tio-col-loose" aria-busy="true" data-test-partner-dashboard-skeleton>
          <span class="sr-only">{{t "reporting.loading"}}</span>
          <div class="h-12 rounded-xl tio-bg-sunken"></div>
          <div class="h-64 rounded-xl tio-bg-sunken"></div>
        </div>
      {{else if this.viewableDashboard}}
        {{! No PageHeader here: the viewer renders the page's one h1 itself. }}
        <DashboardViewPage
          @dashboard={{this.viewableDashboard}}
          @companyId={{this.companyId}}
          @companyName={{this.companyName}}
          @viewedFrom="partner"
          @onBack={{this.handleBack}}
        />
      {{else}}
        <PageHeader @a11yTitle={{t "reporting.dashboards"}} @sticky={{false}} />
        <div class="py-8 text-center" role="alert" data-test-partner-dashboard-unavailable>
          <p class="tio-fg-secondary">{{t "reporting.dashboard_unavailable"}}</p>
          <LinkTo
            @route="authenticated.admin.reporting.index"
            class="tio-text-sm-semibold mt-2 inline-block tio-fg-brand"
          >{{t "reporting.back_to_reports"}}</LinkTo>
        </div>
      {{/if}}
    </VStack>
  </template>
}

export default DashboardViewerRouteComponent;
